import * as React from 'react';
import * as RxSelect from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';

// Themed wrapper over @radix-ui/react-select so all dropdowns in the
// playground share styling + keyboard behaviour. Replaces native <select>
// which couldn't be styled to match warm-beige + brand-green palette.

interface Option {
    value: string;
    label: string;
}

interface Props {
    value: string;
    onValueChange: (v: string) => void;
    options: Option[];
    placeholder?: string;
    disabled?: boolean;
    invalid?: boolean;
    ariaLabel?: string;
    className?: string;
}

export function Select({
    value,
    onValueChange,
    options,
    placeholder = '— pilih —',
    disabled,
    invalid,
    ariaLabel,
    className = '',
}: Props) {
    return (
        <RxSelect.Root value={value} onValueChange={onValueChange} disabled={disabled}>
            <RxSelect.Trigger
                aria-label={ariaLabel}
                aria-invalid={invalid}
                className={`flex h-9 w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 font-mono text-xxs outline-none transition-colors data-[placeholder]:text-ghost focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
                style={{
                    backgroundColor: 'var(--bg-input)',
                    border: `1px solid ${invalid ? 'var(--err)' : 'var(--border)'}`,
                    color: 'var(--ink)',
                }}
            >
                <RxSelect.Value placeholder={placeholder} />
                <RxSelect.Icon asChild>
                    <ChevronDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
                </RxSelect.Icon>
            </RxSelect.Trigger>

            <RxSelect.Portal>
                <RxSelect.Content
                    position="popper"
                    sideOffset={4}
                    className="z-50 min-w-[var(--radix-select-trigger-width)] rounded-md p-1 shadow-lg overflow-hidden"
                    style={{
                        backgroundColor: 'var(--bg)',
                        border: '1px solid var(--border)',
                    }}
                >
                    <RxSelect.Viewport className="max-h-[300px]">
                        {options.map((opt) => (
                            <RxSelect.Item
                                key={opt.value}
                                value={opt.value}
                                className="relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 pl-7 text-xs outline-none data-[highlighted]:bg-bg-hover data-[state=checked]:font-semibold"
                                style={{ color: 'var(--ink)' }}
                            >
                                <RxSelect.ItemIndicator className="absolute left-1.5 inline-flex items-center justify-center">
                                    <Check className="h-3.5 w-3.5" style={{ color: 'var(--accent)' }} />
                                </RxSelect.ItemIndicator>
                                <RxSelect.ItemText>{opt.label}</RxSelect.ItemText>
                            </RxSelect.Item>
                        ))}
                    </RxSelect.Viewport>
                </RxSelect.Content>
            </RxSelect.Portal>
        </RxSelect.Root>
    );
}
